/**
 * Report section scope by declared SoilTestLevel.
 *
 * Free plan cannot generate formal reports; when Professional+ generates
 * a PRELIMINARY report, the snapshot must not present Moderate/Advanced
 * chemistry, sodicity, or medium/long agronomic recommendations as if
 * that evidence were available.
 */

import { SoilTestLevel } from "./soil-domain";

export type ReportChartScope = "texture_only" | "full";

export interface ReportLevelScope {
	level: SoilTestLevel | string;
	/** Full cation / CEC chemistry block. */
	showExchangeChemistry: boolean;
	/** Micronutrients + heavy extras (Advanced panel). */
	showAdvancedAnalytes: boolean;
	/** SAR/ESP sodicity assessment as a primary section. */
	showSodicityAssessment: boolean;
	/** Agronomic categories that need cation panel (CEC, BS, Na, cation balance). */
	showCationAgronomy: boolean;
	/** Texture suitability matrix (uses physics + texture). */
	showTextureSuitability: boolean;
	/** Medium + long recommendation horizons. */
	showMediumLongRecommendations: boolean;
	/** Scientific chart suite beyond USDA texture triangle. */
	chartScope: ReportChartScope;
	/** Labels allowed on PRELIMINARY agronomic category cards. */
	preliminaryAgronomicLabels: readonly string[];
}

const PRELIMINARY_AGRONOMIC_LABELS = [
	"pH",
	"Salinity",
	"Water holding",
	"Drainage",
	"Organic matter",
] as const;

export function reportLevelScope(
	testLevel: string | null | undefined
): ReportLevelScope {
	const level = String(testLevel ?? SoilTestLevel.PRELIMINARY).toUpperCase();

	if (level === SoilTestLevel.ADVANCED || level === "ADVANCED") {
		return {
			level: SoilTestLevel.ADVANCED,
			showExchangeChemistry: true,
			showAdvancedAnalytes: true,
			showSodicityAssessment: true,
			showCationAgronomy: true,
			showTextureSuitability: true,
			showMediumLongRecommendations: true,
			chartScope: "full",
			preliminaryAgronomicLabels: PRELIMINARY_AGRONOMIC_LABELS,
		};
	}

	if (level === SoilTestLevel.MODERATE || level === "MODERATE") {
		return {
			level: SoilTestLevel.MODERATE,
			showExchangeChemistry: true,
			showAdvancedAnalytes: false,
			showSodicityAssessment: true,
			showCationAgronomy: true,
			showTextureSuitability: true,
			showMediumLongRecommendations: true,
			chartScope: "full",
			preliminaryAgronomicLabels: PRELIMINARY_AGRONOMIC_LABELS,
		};
	}

	// PRELIMINARY (default)
	return {
		level: SoilTestLevel.PRELIMINARY,
		showExchangeChemistry: false,
		showAdvancedAnalytes: false,
		showSodicityAssessment: false,
		showCationAgronomy: false,
		showTextureSuitability: true,
		showMediumLongRecommendations: false,
		chartScope: "texture_only",
		preliminaryAgronomicLabels: PRELIMINARY_AGRONOMIC_LABELS,
	};
}
